/**
 * Mock data for Provider Trainings section
 *
 * Assumptions (from Notion Requirements + product context):
 * - Mandatory trainings: HIPAA, FWA, IOP/Group (per IOP/Group Therapy Mandatory Training, FWA in Rippling LMS)
 * - CE period: annual (rolling 12 months) for licensure
 * - Certifications: license + optional certs; expiration/renewal tracked
 */

import type { Certification, HoursSummary, Training } from '../types';

const now = new Date();
const formatDate = (d: Date) => d.toISOString().split('T')[0];
const addDays = (d: Date, days: number) => {
  const out = new Date(d);
  out.setDate(out.getDate() + days);
  return out;
};

export const mockTrainings: Training[] = [
  {
    id: 't1',
    name: 'HIPAA Privacy & Security',
    required: true,
    status: 'completed',
    completionDate: formatDate(addDays(now, -60)),
    expirationDate: formatDate(addDays(now, 305)),
    completedBy: 'Current user',
    verifiedAt: formatDate(addDays(now, -59)),
    verificationStatus: 'verified',
    enforcementNote: 'Required for credentialing',
  },
  {
    id: 't2',
    name: 'Fraud, Waste & Abuse (FWA)',
    required: true,
    status: 'completed',
    completionDate: formatDate(addDays(now, -120)),
    expirationDate: formatDate(addDays(now, 245)),
    completedBy: 'Current user',
    verificationStatus: 'verified',
    enforcementNote: 'Required for credentialing',
  },
  {
    id: 't3',
    name: 'IOP/Group Therapy Mandatory Training',
    required: true,
    status: 'in-progress',
    completionDate: null,
    expirationDate: null,
    enforcementNote: 'Required before supervising IOP/group',
  },
  {
    id: 't4',
    name: 'Cultural Competency (CE)',
    required: false,
    status: 'completed',
    completionDate: formatDate(addDays(now, -30)),
    expirationDate: null,
    completedBy: 'Current user',
    verificationStatus: 'verified',
  },
  {
    id: 't5',
    name: 'Suicide Risk Assessment Annual Update',
    required: true,
    status: 'overdue',
    completionDate: null,
    expirationDate: formatDate(addDays(now, -14)),
    enforcementNote: 'May affect supervision eligibility',
  },
  {
    id: 't6',
    name: 'Ethics in Telehealth',
    required: false,
    status: 'expired',
    completionDate: formatDate(addDays(now, -400)),
    expirationDate: formatDate(addDays(now, -35)),
    completedBy: 'Current user',
  },
];

export const mockCertifications: Certification[] = [
  {
    id: 'c1',
    name: 'LMFT License',
    issuingBody: 'California BBS',
    issueDate: formatDate(addDays(now, -730)),
    expirationDate: formatDate(addDays(now, 335)),
    renewalDate: formatDate(addDays(now, 300)),
    verificationStatus: 'verified',
    verifiedAt: formatDate(addDays(now, -700)),
    daysUntilExpiration: 335,
  },
  {
    id: 'c2',
    name: 'BBS Supervisor Certification',
    issuingBody: 'California BBS',
    issueDate: formatDate(addDays(now, -400)),
    expirationDate: formatDate(addDays(now, 45)),
    renewalDate: formatDate(addDays(now, 30)),
    verificationStatus: 'verified',
    verifiedAt: formatDate(addDays(now, -398)),
    daysUntilExpiration: 45,
  },
  {
    id: 'c3',
    name: 'CPR/BLS',
    issuingBody: 'American Red Cross',
    issueDate: formatDate(addDays(now, -200)),
    expirationDate: formatDate(addDays(now, 165)),
    renewalDate: null,
    verificationStatus: 'pending',
    verifiedAt: null,
    daysUntilExpiration: 165,
    attachmentUrl: '/uploads/cpr-card.pdf',
  },
];

export const mockHoursSummary: HoursSummary = {
  periodLabel: 'CE hours (current cycle)',
  periodStart: formatDate(new Date(now.getFullYear(), 0, 1)),
  periodEnd: formatDate(new Date(now.getFullYear(), 11, 31)),
  requiredHours: 36,
  completedHours: 22,
  remainingHours: 14,
  category: 'CE',
};
